/**
 * Sprint 208 Master item #1 (refinement) — Parent-First Charter Modal.
 *
 * Mounts globally on the parent dashboard. The moment a parent lands
 * without a signed charter, this overlay fires automatically: read the
 * Commitment Charter, type your cursive-styled signature, hit Sign &
 * Generate Invite, then the magic link lands on a "Copy Invitation Link"
 * button. The parent never needs to know they're on a multi-step flow.
 */
import React, { useEffect, useState } from 'react';
import { Shield, FileText, Loader2, Copy, Check, X } from 'lucide-react';
import { apiClient } from '../../api/client';

const CHARTER_TEXT_V1 = (
  "HiBFF Commitment Charter. We agree to use HiBFF as a private space to " +
  "build social fitness — never as social media. Conversations between " +
  "the teen and any AvA are confidential; the parent sees activity and " +
  "progress only. The teen will not share personal contact details inside " +
  "the app, will respect the 9-domain safety guardrails, and will follow " +
  "the 20-challenge journey at their own pace. The parent will respect " +
  "the teen's private practice space and use the parent dashboard to " +
  "celebrate progress, not police content. We commit, together, to " +
  "courage that builds confidence and confidence that builds character."
);

const ParentCharterModal = ({ parentUser, onClose, onSigned }) => {
  const [step, setStep] = useState('sign'); // 'sign' → 'link'
  const [typed, setTyped] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);

  // Probe — only render the modal when the parent has no signed charter yet.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.request('/api/family/parent/charter-status');
        if (cancelled) return;
        if (!data?.signed_by_parent) setShouldShow(true);
      } catch {
        // If the endpoint is unavailable, fall back to showing the modal
        // so the parent can still sign — the backend will dedupe via
        // family_charters.
        if (!cancelled) setShouldShow(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const sign = async () => {
    setError(null);
    if (typed.trim().length < 2) {
      setError('Please type your full name as your signature.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiClient.request('/api/onboarding/parent-first/register-and-sign', {
        method: 'POST',
        body: JSON.stringify({
          name: parentUser?.name || 'Parent',
          email: parentUser?.email,
          phone: parentUser?.phone || parentUser?.mobile || '0000000000',
          typed_signature: typed.trim(),
        }),
      });
      setResult(data);
      setStep('link');
      if (onSigned) onSigned(data);
    } catch (e) {
      setError(e?.message || 'Could not sign charter. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const magicLinkFullUrl = result?.magic_link
    ? `${window.location.origin}${result.magic_link.magic_link_path}`
    : '';

  const copy = async () => {
    if (!magicLinkFullUrl) return;
    try {
      await navigator.clipboard.writeText(magicLinkFullUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (_e) {}
  };

  if (!shouldShow) return null;

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/55 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="parent-charter-modal"
    >
      <div className="bg-white rounded-3xl max-w-xl w-full p-7 sm:p-9 my-8 relative shadow-2xl">
        <button
          onClick={() => { setShouldShow(false); if (onClose) onClose(); }}
          className="absolute top-4 right-4 p-1.5 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-700"
          aria-label="Dismiss"
          data-testid="parent-charter-modal-close"
        ><X size={18} /></button>

        {step === 'sign' && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <FileText size={22} className="text-purple-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">HiBFF Commitment Charter</h2>
                <p className="text-xs text-gray-500">Sign once to activate your family on HiBFF</p>
              </div>
            </div>

            <div className="bg-gradient-to-br from-amber-50 via-white to-pink-50 border border-amber-200 rounded-2xl p-5 text-sm leading-relaxed text-gray-700 mb-5 max-h-64 overflow-y-auto" data-testid="parent-charter-modal-text">
              {CHARTER_TEXT_V1}
            </div>

            <label className="block mb-1">
              <span className="text-[11px] font-bold text-gray-600 uppercase tracking-wide">Parent digital signature</span>
              <input
                type="text"
                value={typed}
                onChange={e => setTyped(e.target.value)}
                placeholder="Type your full name"
                className="mt-1 w-full px-4 py-3 border-2 border-purple-300 rounded-xl focus:border-purple-500 focus:ring-2 focus:ring-purple-100 outline-none text-2xl"
                style={{ fontFamily: 'Caveat, "Brush Script MT", cursive', fontStyle: 'italic' }}
                data-testid="parent-charter-modal-signature"
                autoFocus
              />
            </label>
            <p className="text-[11px] text-gray-400 mb-4">Typing your name is your binding digital signature.</p>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-3" data-testid="parent-charter-modal-error">{error}</div>
            )}

            <button
              onClick={sign}
              disabled={submitting}
              className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-xl hover:shadow-lg disabled:opacity-50 inline-flex items-center justify-center gap-2"
              data-testid="parent-charter-modal-sign"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : <>Sign &amp; Generate Invite <Shield size={16} /></>}
            </button>
          </>
        )}

        {step === 'link' && result && (
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-green-400 to-emerald-500 rounded-2xl flex items-center justify-center">
              <Check size={32} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-1">Charter signed</h2>
            <p className="text-sm text-gray-500 mb-5">Copy the secure invite link and send it to your teen via your preferred channel. Link expires in 7 days.</p>
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4 mb-4 text-left break-all text-sm font-mono text-gray-700" data-testid="parent-charter-modal-link">
              {magicLinkFullUrl}
            </div>
            <button
              onClick={copy}
              className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-500 text-white font-semibold rounded-xl hover:shadow-lg inline-flex items-center justify-center gap-2 mb-3"
              data-testid="parent-charter-modal-copy"
            >
              {copied ? <><Check size={16} /> Copied!</> : <><Copy size={16} /> Copy Invitation Link</>}
            </button>
            <button
              onClick={() => { setShouldShow(false); if (onClose) onClose(); }}
              className="text-sm text-gray-500 hover:text-gray-700"
              data-testid="parent-charter-modal-done"
            >Done — back to dashboard</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ParentCharterModal;
